import {css, Typography, useTheme} from '@mui/material';
import SvgIcon from '@mui/material/SvgIcon';
import * as React from 'react';
import {defineMessages, FormattedMessage, useIntl, WrappedComponentProps} from 'react-intl';

import {FixedPaginationButton} from './FixedPaginationButton';
import {PageInput} from './PageInput';
import {PageSizeInput} from './PageSizeInput';
import {ResultCount} from './ResultCount';
import FirstPageArrow from 'src/assets/icons/haidiFirstPageIcon.svg';
import LastPageArrow from 'src/assets/icons/haidiLastPageIcon.svg';
import NextPageArrow from 'src/assets/icons/haidiNextPageIcon.svg';
import PrevPageArrow from 'src/assets/icons/haidiPrevPageIcon.svg';
import {GetStylesFunction} from 'src/js/common/types';
import {TranslateUtils} from 'src/js/common/utils';

const I18N = defineMessages({
  FIRST_PAGE: {
    id: 'common.page.first',
    defaultMessage: 'First page',
    description: 'text for first page',
  },
  PREV_PAGE: {
    id: 'common.page.previous',
    defaultMessage: 'Previous page',
    description: 'text for previous page',
  },
  NEXT_PAGE: {
    id: 'common.page.next',
    defaultMessage: 'Next page',
    description: 'text for next page',
  },
  LAST_PAGE: {
    id: 'common.page.last',
    defaultMessage: 'Last page',
    description: 'text for last page',
  },
});

interface OwnProps {
  activePage: number;
  activeSize?: number;
  countInPage?: number;
  handlePageChange: (page: number) => void;
  handlePageSizeChange?: (size: number) => void;
  pages: number;
  pageSize?: number;
  resultCount?: number;
  showPageSize?: boolean;
  showResultCount?: boolean;
  showMoveToPage?: boolean;
  /** Max number of page-number buttons shown in the sliding window (default 4). Use a smaller value for
   * narrow panels (e.g. the document list panel uses 2). Pages beyond the window are reached via the arrows. */
  maxPageClickers?: number;
}

type Props = OwnProps & WrappedComponentProps;

export const FixedResultPaginationBig: React.FC<Props> = (props) => {
  const {
    activePage,
    activeSize,
    countInPage,
    handlePageChange,
    handlePageSizeChange,
    pages,
    pageSize,
    resultCount,
    showPageSize = false,
    showResultCount = false,
    showMoveToPage = true,
    maxPageClickers = 4,
  } = props;
  const theme = useTheme();
  const styles = getStyles({theme, params: {showPageSize, showMoveToPage}});
  const intl = useIntl();
  const pageClickerPages = React.useMemo(() => {
    const accPages: number[] = [];
    const clickerCount = Math.max(1, maxPageClickers);
    let startPage = Math.max(1, activePage - 1);
    const endPage = Math.min(startPage + clickerCount - 1, pages);
    startPage = Math.max(1, endPage - clickerCount + 1);
    for (let page = startPage; page <= endPage; page++) {
      accPages.push(page);
    }
    return accPages;
  }, [activePage, pages, maxPageClickers]);
  const pageClickers = pageClickerPages.map((v) => {
    let className = '';
    if (v === activePage) className += ' active';
    if (v > pages) className += ' disabled';
    return (
      <span key={v} css={styles.pageClicker} className={className.trim()} onClick={() => handlePageChange(v)} onKeyDown={undefined}>
        <Typography variant='value'>{v}</Typography>
      </span>
    );
  });
  return (
    <div data-name='paging-wrapper' css={styles.dlResultPagination}>
      {showPageSize || showResultCount ? (
        <span data-name='paging-size' css={styles.dlResultCount}>
          {showPageSize && activeSize && handlePageSizeChange ? (
            <PageSizeInput activeSize={activeSize} onChange={handlePageSizeChange} textBefore isBig />
          ) : null}
          {showResultCount && resultCount && pageSize && countInPage ? (
            <ResultCount count={resultCount} pageSize={pageSize} countInPage={countInPage} activePage={activePage} isBig />
          ) : null}
        </span>
      ) : null}
      <span data-name='paging-buttons' css={styles.dlResultPages}>
        {showMoveToPage && (
          <span css={styles.dlPaginationInputWrapper}>
            <Typography data-name={'fixed-result-pagination--page-from-before'} css={styles.dlPageInfoBefore} variant={'caption'}>
              <FormattedMessage id={'common.pageFromBefore'} defaultMessage={'Go to page'} description={'label before Page number'} />
            </Typography>
            <Typography variant={'caption'} data-name='fixed-result-pagination--page-input'>
              <PageInput activePage={activePage} onChange={handlePageChange} pages={pages} isBig />
            </Typography>
            <Typography data-name={'fixed-result-pagination--page-from'} css={styles.dlPageInfo} variant={'caption'}>
              <FormattedMessage
                id={'common.pageFrom'}
                defaultMessage={'of {pages}'}
                description={'label Page number'}
                values={{pages: <>{pages}</>}}
              />
            </Typography>
          </span>
        )}
        <span css={styles.leftArrowWrapper}>
          <FixedPaginationButton
            dataName='fixed-result-pagination--button-first-page'
            onClick={handlePageChange}
            disabled={activePage <= 1}
            pageTo={1}
            title={TranslateUtils.translate({...I18N.FIRST_PAGE}, intl)}
            isBig
          >
            <SvgIcon component={FirstPageArrow} viewBox='0 0 12 12' css={styles.firstPageArrow} />
          </FixedPaginationButton>
          <FixedPaginationButton
            dataName='fixed-result-pagination--button-prev-page'
            onClick={handlePageChange}
            disabled={activePage <= 1}
            pageTo={activePage - 1}
            title={TranslateUtils.translate({...I18N.PREV_PAGE}, intl)}
            isBig
          >
            <SvgIcon component={PrevPageArrow} viewBox='0 0 12 12' css={styles.prevPageArrow} />
          </FixedPaginationButton>
        </span>
        <span css={styles.pageClickerWrapper}>{pageClickers}</span>
        <span css={styles.rightArrowWrapper}>
          <FixedPaginationButton
            dataName='fixed-result-pagination--button-next-page'
            onClick={handlePageChange}
            disabled={activePage >= pages}
            pageTo={activePage + 1}
            title={TranslateUtils.translate({...I18N.NEXT_PAGE}, intl)}
            isBig
          >
            <SvgIcon component={NextPageArrow} viewBox='0 0 12 12' css={styles.nextPageArrow} />
          </FixedPaginationButton>
          <FixedPaginationButton
            dataName='fixed-result-pagination--button-last-page'
            onClick={handlePageChange}
            disabled={activePage >= pages}
            pageTo={pages}
            title={TranslateUtils.translate({...I18N.LAST_PAGE}, intl)}
            isBig
          >
            <SvgIcon component={LastPageArrow} viewBox='0 0 12 12' css={styles.lastPageArrow} />
          </FixedPaginationButton>
        </span>
      </span>
    </div>
  );
};

FixedResultPaginationBig.displayName = 'FixedResultPaginationBig';

const getStyles: GetStylesFunction = (options) => {
  const isPageSizeShown = options?.params?.showPageSize;
  const isMoveToPageShown = options?.params?.showMoveToPage;
  return {
    dlPaginationInputWrapper: css({
      marginRight: `${isPageSizeShown ? options?.theme?.spacing(3) : options?.theme?.spacing(1)}`,
      display: 'flex',
      alignItems: 'center',
    }),
    lastPageArrow: css`
      height: 12px;
      width: 12px;
      fill: none;
    `,
    nextPageArrow: css`
      height: 12px;
      width: 12px;
      fill: none;
    `,
    firstPageArrow: css`
      height: 12px;
      width: 12px;
      fill: none;
    `,
    prevPageArrow: css`
      height: 12px;
      width: 12px;
      fill: none;
    `,
    dlResultPagination: css`
      display: flex;
      align-items: center;
      justify-content: ${isMoveToPageShown ? (isPageSizeShown ? 'space-between' : 'flex-end') : 'space-evenly'};
      order: 1;
      position: relative;
      min-height: 32px;
    `,
    dlResultCount: css`
      margin-left: var(--dlMarginSmall);
      margin-right: ${isPageSizeShown ? 0 : '7px'};
    `,
    dlPageLink: css`
      padding: 0 5px;
      margin-right: 3px;

      &:hover,
      &:focus,
      &:active:focus,
      &:active:hover {
        z-index: 2;
        outline: none;
      }
    `,
    dlPageInfoBefore: css`
      color: var(--dlAgGridGreySubtitleColor);
      white-space: nowrap;
      margin-right: 5px;
    `,
    dlPageInfo: css`
      display: none;
      margin-right: 5px;
      white-space: nowrap;
    `,
    dlPageLinkDisabled: css`
      &,
      &:hover,
      &:focus,
      &:active:focus,
      &:active:hover {
        cursor: not-allowed;
      }
    `,
    dlResultPages: css`
      margin: 0 var(--dlMarginExtraSmall);
      right: 0;
      display: flex;
      align-items: center;

      :global ul.pagination {
        margin: 0;

        li {
          a,
          button {
            .dlPageLink();
            color: var(--dlButtonFont);
            background-color: var(--dlPageButtonInactiveBackgroundColor);
            border: 1px solid var(--dlPageButtonInactiveBackgroundColor);
            border-radius: 10px;

            &:hover,
            &:focus,
            &:active:focus,
            &:active:hover {
              color: var(--dlButtonFont);
              background-color: var(--dlHighlightedColor);
              border-color: var(--dlHighlightedColor);
            }
          }
        }

        :global li.active {
          a,
          button {
            color: var(--dlButtonFont);
            background-color: var(--dlPageButtonBackgroundColor);
            border-color: var(--dlPageButtonBackgroundColor);
          }
        }

        :global li.disabled {
          a,
          button {
            .dlPageLinkDisabled();

            &,
            &:hover,
            &:focus,
            &:active:focus,
            &:active:hover {
              background-color: var(--dlContentBackgroundColor);
              border-color: var(--dlContentBackgroundColor);
            }
          }
        }
      }
    `,
    leftArrowWrapper: css`
      display: inline-flex;
      gap: 8px;
      margin-right: 16px;
    `,
    pageClickerWrapper: css`
      display: inline-flex;
    `,
    pageClicker: css`
      width: 32px;
      height: 32px;
      border-radius: 8px;
      display: inline-flex;
      justify-content: center;
      align-items: center;
      user-select: none;
      background: var(--dlAgGridPageClickerBackground);
      color: var(--dlAgGridPageClickerColor);

      &:not(.disabled):hover,
      &.active {
        background: var(--dlAgGridPageClickerSelectedBackground);
        color: var(--dlAgGridPageClickerSelectedColor);
        cursor: pointer;
      }
      &.disabled {
        color: var(--dlAgGridGreySubtitleColor);
        cursor: not-allowed;
        pointer-events: none;
      }
    `,
    rightArrowWrapper: css`
      display: inline-flex;
      gap: 8px;
      margin-left: 16px;
    `,
  };
};
